import { useState } from "react";
import { FaPlus } from "react-icons/fa";
import AdminService from "../services/adminService";
import LoadingForButton from "./LoadingForButton";
import { useDispatch, useSelector } from "react-redux";
import { refreshDataGuest } from "../store/guestSlice";
import { useTranslation } from "react-i18next";
import UpgradePrompt from "./UpgradePrompt";

const TABLE_FREE_LIMIT = 10;

const AddTable = ({ tables = [], guests, setActiveForm }) => {
    const { t } = useTranslation();
    const user = useSelector((state) => state.userStore.user);

    const [formData, setFormData] = useState({ name: "", seats: "", isPrivateTable: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [invalidCredentials, setInvalidCredentials] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const dispatch = useDispatch();

    const isPremium = user?.subscription === "premium" && user?.subscriptionStatus === "active";
    const isAtLimit = !isPremium && tables.length >= TABLE_FREE_LIMIT;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: false }));
        setInvalidCredentials("");
        setSuccessMessage("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isAtLimit) return;

        setLoading(true);
        setErrors({});

        const cleanedName = formData.name.replace(/^(stol[\s:#-]*)+/i, "").trim();

        const newErrors = {};
        const nameRegex = /^[a-zA-Z0-9čćđšžČĆĐŠŽ\s-]+$/;

        if (!cleanedName) {
            newErrors.name = t("tableNameRequired");
        } else if (!nameRegex.test(cleanedName)) {
            newErrors.name = t("tableNameInvalid");
        } else if (cleanedName.length > 50) {
            newErrors.name = t("maxLengthError", { count: 50 });
        }

        const seatsValue = parseInt(formData.seats, 10);
        if (!formData.seats) {
            newErrors.seats = t("tableSeatsRequired");
        } else if (isNaN(seatsValue) || seatsValue <= 0 || seatsValue > 100) {
            newErrors.seats = t("tableSeatsInvalid");
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setLoading(false);
            return;
        }

        AdminService.addTable({ ...formData, name: cleanedName })
            .then(() => {
                setLoading(false);
                dispatch(refreshDataGuest());
                setFormData({ name: "", seats: "", isPrivateTable: false });
                setSuccessMessage(t("tableAddedSuccess"));
                setTimeout(() => setSuccessMessage(""), 3000);
            })
            .catch((err) => {
                //console.log(err);
                if (err.response?.data?.message.includes("already exists")) {
                    setInvalidCredentials(t("tableAlreadyExists"));
                } else if (err.response?.data?.message.includes("table limit")) {
                    setInvalidCredentials(t("tableLimitReached"));
                } else {
                    setInvalidCredentials(t("genericError"));
                }
                setLoading(false);
            });
    };

    return (
        <div className="flex justify-center pt-20 pb-10 lg:ml-[300px] overflow-y-auto">
            <div className="relative p-4 rounded-lg w-full max-w-2xl mx-auto">
                <h2 className="text-2xl font-semibold text-center mb-5 flex items-center justify-center">
                    <FaPlus className="mr-2 text-gray-700" /> {t("addTableTitle")}
                </h2>
                <p className="text-gray-600 mb-7">{t("addTableSubtitle")}</p>

                {isAtLimit && (
                    <UpgradePrompt
                        reason="table"
                        currentCount={tables.length}
                        limit={TABLE_FREE_LIMIT}
                        otherCount={guests.length}
                        setActiveForm={() => setActiveForm("addGuest")}
                    />
                )}

                <form onSubmit={handleSubmit} className={`flex flex-col space-y-7 mb-10 ${isAtLimit ? "opacity-50 pointer-events-none select-none" : ""}`}>
                    <fieldset disabled={isAtLimit} className="flex flex-col space-y-7">
                        <div className="flex flex-col">
                            <label className="font-semibold flex items-center mb-1">{t("tableNameLabel")} *</label>
                            <input
                                type="text"
                                name="name"
                                maxLength={50}
                                placeholder="5"
                                autoFocus={!isAtLimit}
                                value={formData.name}
                                onChange={handleChange}
                                className={`inputWedding !pl-4 ${errors.name ? "!border-red-500" : ""}`}
                            />
                            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                        </div>

                        <div className="flex flex-col">
                            <label className="font-semibold flex items-center mb-1">{t("tableSeatsLabel")} *</label>
                            <input
                                type="number"
                                name="seats"
                                min={1}
                                max={20}
                                placeholder="10"
                                value={formData.seats}
                                onChange={handleChange}
                                className={`inputWedding !pl-4 ${errors.seats ? "!border-red-500" : ""}`}
                            />
                            {errors.seats && <p className="text-red-500 text-sm">{errors.seats}</p>}
                        </div>

                        <div className="flex items-center mt-2">
                            <input
                                type="checkbox"
                                id="isPrivateTable"
                                name="isPrivateTable"
                                checked={formData.isPrivateTable}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, isPrivateTable: e.target.checked }))
                                }
                                className="w-5 h-5 accent-blue-700 rounded border-gray-300"
                            />
                            <label htmlFor="isPrivateTable" className="ml-3 text-gray-800 font-medium select-none">
                                {t("privateTableLabel")}
                            </label>
                        </div>

                        {invalidCredentials && (
                            <p className="bg-red-100 rounded-lg p-2 text-red-700 text-center my-4">
                                {invalidCredentials}
                            </p>
                        )}

                        {successMessage && (
                            <p className="bg-green-100 rounded-lg p-2 text-green-700 text-center my-4">
                                {successMessage}
                            </p>
                        )}

                        <button type="submit" disabled={loading || isAtLimit} className="submitButton w-full">
                            {loading ? <LoadingForButton /> : t("addTableButton")}
                        </button>
                    </fieldset>
                </form>
            </div>
        </div>
    );
};

export default AddTable;
